// Presupuestos: la cuenta y la hoja que se imprime (o se guarda como PDF).
//
// El presupuesto NO se guarda en el sistema: se arma en pantalla con el catálogo que ya trajo
// `product/all` y sale por el diálogo de impresión del navegador ("Guardar como PDF"), igual
// que el listado de anotados (printAdmissions) y la comanda. Por eso todo esto es del lado del
// cliente y no hay backend.
//
// El diseño es el "Tarjeta limpia" de los mails (backend/src/utils/email/layout/tarjeta.js):
// mismo logo de letras oscuras, misma paleta y la franja de los cuatro colores. Si el mail
// cambia de paleta, este archivo se toca a mano — no comparten código porque viven en repos
// distintos.

import { convertToMoney } from './formats';
import { escapeHtml } from './printAdmissions';

export const VALIDEZ_DIAS_DEFAULT = 15;

export const CONDICIONES_DEFAULT = [
    'Precios en pesos uruguayos, IVA incluido.',
    'Sujeto a disponibilidad de stock al momento de confirmar el pedido.',
    'Retiro en nuestro local o envío a coordinar.',
];

// Los mismos datos que muestra la web (frontend/src/config/storeInfo.ts).
export const LOCAL = {
    nombre: 'Enigma Games',
    direccion: 'Pablo de María 1301, Montevideo',
    telefono: '099 089 892',
    mail: 'info@enigmagames.uy',
    web: 'enigmagames.uy',
    instagram: '@enigmagamesuy',
};

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
    'septiembre', 'octubre', 'noviembre', 'diciembre'];

// Con partes LOCALES, nunca toISOString(): de noche el día en UTC ya es mañana y el
// presupuesto saldría fechado un día después.
export function fechaLarga(fecha) {
    return `${fecha.getDate()} de ${MESES[fecha.getMonth()]} de ${fecha.getFullYear()}`;
}

export function fechaCorta(fecha) {
    const dos = (n) => String(n).padStart(2, '0');
    return `${fecha.getFullYear()}-${dos(fecha.getMonth() + 1)}-${dos(fecha.getDate())}`;
}

export function sumarDias(fecha, dias) {
    return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate() + dias);
}

// El precio al que se cotiza: el de lista con el descuento propio de la ficha, que es lo que
// el cliente ve en la web. NO pasa por `calculatePrice`: el presupuesto no sabe con qué va a
// pagar, así que el descuento por efectivo/transferencia no entra.
export function precioDeLista(producto) {
    const precio = Number(producto && producto.price);
    const descuento = Number(producto && producto.discount) || 0;
    if (!Number.isFinite(precio) || precio <= 0) {
        return 0;
    }
    return Math.round(precio * (1 - descuento / 100));
}

export function cantidadValida(valor) {
    const n = Math.floor(Number(valor));
    return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * Cruza los renglones que guarda la pantalla con el catálogo y decide cuáles se cotizan.
 *
 * Un renglón del catálogo guarda sólo el código: el nombre, el precio y el stock se leen del
 * catálogo cada vez, así un presupuesto que quedó abierto no cotiza con el precio de ayer.
 *
 *   · con stock para la cantidad pedida            -> se cotiza
 *   · sin stock (o no alcanza)                     -> "Sin stock", afuera del total
 *   · sin stock pero marcado "cotizar igual"       -> se cotiza (se va a encargar)
 *   · renglón libre (no está en el catálogo)       -> se cotiza sólo si le pusieron precio
 *
 * @param {Array} renglones     [{ key, code|null, nombre, cantidad, precioManual, cotizarIgual }]
 * @param {Map}   catalogo      Map<String(code), producto de product/all>
 */
export function resolverRenglones(renglones, catalogo) {
    return (renglones || []).map((renglon) => {
        const cantidad = cantidadValida(renglon.cantidad);
        const producto = renglon.code != null && catalogo ? catalogo.get(String(renglon.code)) : null;
        if (producto) {
            const precio = precioDeLista(producto);
            const stock = Number(producto.stock) || 0;
            const alcanza = stock >= cantidad;
            return {
                key: renglon.key,
                nombre: producto.name,
                codigo: `${producto.prefix || ''}${producto.code}`,
                usado: Number(producto.productType) === 3,
                libre: false,
                cantidad,
                precio,
                stock,
                alcanza,
                cotizarIgual: !!renglon.cotizarIgual,
                cotiza: precio > 0 && (alcanza || !!renglon.cotizarIgual),
            };
        }
        // Libre, o un producto que ya no está en el catálogo (se borró con el presupuesto abierto).
        const manual = Math.round(Number(renglon.precioManual));
        const precio = Number.isFinite(manual) && manual > 0 ? manual : 0;
        return {
            key: renglon.key,
            nombre: renglon.nombre || '',
            codigo: null,
            usado: false,
            libre: true,
            cantidad,
            precio,
            stock: null,
            alcanza: false,
            cotizarIgual: false,
            cotiza: precio > 0,
        };
    });
}

export function porcentajeValido(valor) {
    const n = Number(valor);
    if (!Number.isFinite(n) || n <= 0) {
        return 0;
    }
    return Math.min(n, 100);
}

/** La cuenta. Todo en pesos enteros: `convertToMoney` imprime los decimales si los hay. */
export function calcularPresupuesto(resueltos, descuentoPct) {
    const cotizados = (resueltos || []).filter((r) => r.cotiza);
    const subtotal = cotizados.reduce((total, r) => total + r.precio * r.cantidad, 0);
    const pct = porcentajeValido(descuentoPct);
    const descuento = Math.round(subtotal * pct / 100);
    return {
        cotizados: cotizados.length,
        sinStock: (resueltos || []).length - cotizados.length,
        subtotal,
        descuentoPct: pct,
        descuento,
        total: subtotal - descuento,
    };
}

const pesos = (n) => `$ ${convertToMoney(Math.round(n))}`;
const plural = (n, uno, muchos) => `${n} ${n === 1 ? uno : muchos}`;

/** El nombre que el navegador propone al "Guardar como PDF" (sale del <title>). */
export function tituloDelArchivo({ cliente, fecha }) {
    const para = String(cliente || '').trim();
    return ['Presupuesto', para, fechaCorta(fecha || new Date())].filter(Boolean).join(' - ');
}

const ESTILOS = `
@page { size: A4; margin: 0 }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact }
html, body { margin: 0; padding: 0; background: #fff }
body { font-family: 'Poppins', Arial, Helvetica, sans-serif; color: #3b4757; font-size: 9pt; line-height: 1.4 }
/* La hoja es una tabla para que el margen de arriba y el de abajo se repitan en CADA página
   (thead/tfoot): con @page margin 0 —que es lo que evita que el navegador imprima su fecha y
   su URL— una segunda página arrancaría pegada al borde del papel. */
table { border-collapse: collapse }
.hoja { width: 210mm }
.hoja > thead > tr > td, .hoja > tfoot > tr > td { height: 11mm; padding: 0 }
.hoja > tbody > tr > td { padding: 0 16mm; vertical-align: top }
.cuerpo { position: relative; min-height: 274mm; padding-bottom: 11mm }
header { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 4mm }
.logo { width: 44mm; height: auto; display: block }
.doc { text-align: right }
.etiqueta { font-size: 7.6pt; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: #66758a }
.doc .fecha { font-size: 10.5pt; font-weight: 600; color: #16202e; margin-top: .6mm }
.franja { display: flex; height: 1.5mm; border-radius: 1mm; overflow: hidden }
.franja i { flex: 1 }
h1 { font-size: 17.5pt; line-height: 1.2; font-weight: 700; color: #16202e; margin: 5.5mm 0 3.6mm; letter-spacing: -.01em }
.datos { display: flex; background: #f5f8fc; border: 1px solid #dde3ec; border-radius: 3mm; padding: 2.5mm 5mm; margin-bottom: 4.2mm }
.datos div { flex: 1 }
.datos b { display: block; font-weight: 600; color: #16202e; font-size: 10pt; margin-top: .4mm }
.lista { width: 100% }
.lista th { font-size: 7.4pt; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: #66758a; text-align: left; padding: 0 2.5mm 1.8mm; border-bottom: 1.5px solid #16202e }
.lista td { padding: .58mm 2.5mm; border-bottom: 1px solid #e3e9f1; vertical-align: middle }
.lista tr { break-inside: avoid; page-break-inside: avoid }
.lista th.num, .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums }
.lista th.cant, .cant { text-align: center; width: 16mm }
.idx { width: 9mm; color: #66758a; font-size: 8.2pt; font-variant-numeric: tabular-nums }
.juego { color: #16202e; font-weight: 500 }
.fuerte { color: #16202e; font-weight: 600 }
.agotado .juego { color: #66758a; font-weight: 400 }
.vacio { text-align: center; color: #66758a; padding: 6mm 0 }
.pill { display: inline-block; font-size: 7pt; font-weight: 600; letter-spacing: .09em; text-transform: uppercase; color: #66758a; background: #f5f8fc; border: 1px solid #dde3ec; border-radius: 10mm; padding: .1mm 2.6mm }
.cierre { display: flex; gap: 6mm; margin-top: 4.5mm; align-items: flex-start; break-inside: avoid; page-break-inside: avoid }
.notas { flex: 1; font-size: 8.4pt; color: #66758a; line-height: 1.55 }
.notas ul { margin: 1.2mm 0 0; padding-left: 4mm }
.aviso { background: #fff8ea; border: 1px solid #FABA58; color: #5a4a1f; border-radius: 2.5mm; padding: 2.4mm 3.5mm; font-size: 8.4pt; margin-bottom: 3mm }
.totales { width: 76mm; background: #f5f8fc; border: 1px solid #dde3ec; border-radius: 3mm; padding: 3mm 5mm 3.4mm }
.totales table { width: 100% }
.totales td { padding: .9mm 0; font-size: 9pt }
.totales .total td { border-top: 1.5px solid #16202e; padding-top: 2.2mm; font-size: 13.5pt; font-weight: 700; color: #16202e }
.totales .total td:first-child { font-size: 9pt; font-weight: 600; letter-spacing: .1em; text-transform: uppercase }
.totales .sep td { padding: .8mm 0 }
.suave { color: #66758a }
footer { position: absolute; left: 0; right: 0; bottom: 0; border-top: 1px solid #e3e9f1; padding-top: 2.6mm; font-size: 7.8pt; color: #66758a; display: flex; justify-content: space-between }
footer b { color: #16202e; font-weight: 600 }
`;

const fuentes = (base) => [400, 500, 600, 700].map((peso) =>
    `@font-face{font-family:'Poppins';font-weight:${peso};font-style:normal;font-display:block;`
    + `src:url('${base}/fonts/poppins-latin-${peso}-normal.woff2') format('woff2')}`
).join('\n');

function filaDeRenglon(renglon, indice) {
    const nombre = `${escapeHtml(renglon.nombre)}${renglon.usado ? ' <span class="suave">(usado)</span>' : ''}`;
    if (!renglon.cotiza) {
        return `<tr class="agotado"><td class="idx">${indice + 1}</td><td class="juego">${nombre}</td>`
            + '<td class="cant"></td><td class="num" colspan="2"><span class="pill">Sin stock</span></td></tr>';
    }
    return `<tr><td class="idx">${indice + 1}</td><td class="juego">${nombre}</td>`
        + `<td class="cant">${renglon.cantidad}</td><td class="num">${pesos(renglon.precio)}</td>`
        + `<td class="num fuerte">${pesos(renglon.precio * renglon.cantidad)}</td></tr>`;
}

/**
 * La hoja completa, como documento HTML. Pura: no toca el DOM ni lee el reloj si le pasan
 * `fecha`, así los tests la arman con datos fijos.
 *
 * @param {Object} datos
 * @param {string} datos.cliente
 * @param {string} [datos.titulo]       si falta, "Presupuesto para <cliente>"
 * @param {Date}   [datos.fecha]
 * @param {number} [datos.validezDias]  0 o vacío = no se imprime "válido hasta"
 * @param {Array}  datos.renglones      la salida de `resolverRenglones`
 * @param {number} [datos.descuentoPct]
 * @param {string[]} [datos.condiciones]
 * @param {string} [datos.assetsBase]   origen de /fonts y /images (el del backoffice)
 */
export function buildPresupuestoHtml(datos) {
    const {
        cliente = '', titulo = '', fecha = new Date(), validezDias = VALIDEZ_DIAS_DEFAULT,
        renglones = [], descuentoPct = 0, condiciones = CONDICIONES_DEFAULT, assetsBase = '',
    } = datos || {};
    const para = String(cliente).trim();
    const encabezado = String(titulo).trim() || (para ? `Presupuesto para ${para}` : 'Presupuesto');
    const cuenta = calcularPresupuesto(renglones, descuentoPct);
    const dias = Math.floor(Number(validezDias));
    const base = String(assetsBase).replace(/\/+$/, '');

    const celdasDeDatos = [
        `<div><span class="etiqueta">Para</span><b>${escapeHtml(para) || '&mdash;'}</b></div>`,
        `<div><span class="etiqueta">Fecha</span><b>${fechaLarga(fecha)}</b></div>`,
    ];
    if (Number.isFinite(dias) && dias > 0) {
        celdasDeDatos.push(`<div><span class="etiqueta">Válido hasta</span><b>${fechaLarga(sumarDias(fecha, dias))}</b></div>`);
    }

    const filas = renglones.length
        ? renglones.map(filaDeRenglon).join('')
        : '<tr><td class="vacio" colspan="5">Todavía no hay juegos en el presupuesto.</td></tr>';

    const aviso = cuenta.sinStock > 0
        ? `<div class="aviso"><b>${plural(cuenta.sinStock, 'juego', 'juegos')} sin stock por el momento.</b> `
            + `No ${cuenta.sinStock === 1 ? 'está incluido' : 'están incluidos'} en el total.</div>`
        : '';
    const notas = (condiciones || []).map((c) => String(c).trim()).filter(Boolean);
    const listaDeCondiciones = notas.length
        ? `<span class="etiqueta">Condiciones</span><ul>${notas.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul>`
        : '';

    const filasDeTotales = [
        `<tr><td>Subtotal <span class="suave">&middot; ${plural(cuenta.cotizados, 'juego', 'juegos')}</span></td>`
            + `<td class="num">${pesos(cuenta.subtotal)}</td></tr>`,
    ];
    if (cuenta.descuento > 0) {
        // El porcentaje con coma decimal, como se escribe acá (7,5 %).
        const pct = String(cuenta.descuentoPct).replace('.', ',');
        filasDeTotales.push(`<tr><td>Descuento <span class="suave">&middot; ${pct} %</span></td>`
            + `<td class="num">&minus; ${pesos(cuenta.descuento)}</td></tr>`);
    }

    return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escapeHtml(tituloDelArchivo({ cliente: para, fecha }))}</title>
<style>${fuentes(base)}${ESTILOS}</style>
</head>
<body>
<table class="hoja"><thead><tr><td></td></tr></thead><tfoot><tr><td></td></tr></tfoot><tbody><tr><td><div class="cuerpo">
<header><img class="logo" src="${base}/images/logo-enigma.svg" alt="${LOCAL.nombre}"><div class="doc"><div class="etiqueta">Presupuesto</div><div class="fecha">${fechaLarga(fecha)}</div></div></header>
<div class="franja"><i style="background:#1CBFA0"></i><i style="background:#0069FF"></i><i style="background:#FF0070"></i><i style="background:#FABA58"></i></div>
<h1>${escapeHtml(encabezado)}</h1>
<div class="datos">${celdasDeDatos.join('')}</div>
<table class="lista">
<thead><tr><th></th><th>Juego</th><th class="cant">Cant.</th><th class="num">Precio unitario</th><th class="num">Subtotal</th></tr></thead>
<tbody>${filas}</tbody>
</table>
<div class="cierre">
<div class="notas">${aviso}${listaDeCondiciones}</div>
<div class="totales"><table>${filasDeTotales.join('')}<tr class="sep"><td colspan="2"></td></tr><tr class="total"><td>Total</td><td class="num">${pesos(cuenta.total)}</td></tr></table></div>
</div>
<footer><span><b>${LOCAL.nombre}</b> &middot; ${LOCAL.direccion}</span><span>${LOCAL.telefono} &middot; ${LOCAL.mail} &middot; ${LOCAL.web} &middot; ${LOCAL.instagram}</span></footer>
</div></td></tr></tbody></table>
</body>
</html>`;
}

/**
 * Abre el diálogo de impresión sobre el iframe de la vista previa.
 *
 * Espera a que Poppins termine de cargar ADENTRO del iframe: `print()` en el onload saldría
 * con la tipografía de reemplazo, porque las fuentes de un @font-face no frenan el evento
 * load. Y le presta el título al documento de afuera mientras dura el diálogo, porque según
 * el navegador el nombre propuesto para el PDF sale de uno o del otro.
 */
export function imprimirPresupuesto(frame, titulo) {
    const ventana = frame && frame.contentWindow;
    if (!ventana) {
        return;
    }
    const lanzar = () => {
        const anterior = document.title;
        document.title = titulo || anterior;
        try {
            ventana.focus();
            ventana.print();
        } catch (e) {
            // noop: si el navegador rechaza el print no hay nada que limpiar salvo el título
        }
        setTimeout(() => { document.title = anterior; }, 1000);
    };
    const fuentesDelFrame = frame.contentDocument && frame.contentDocument.fonts;
    if (fuentesDelFrame && fuentesDelFrame.ready && typeof fuentesDelFrame.ready.then === 'function') {
        fuentesDelFrame.ready.then(lanzar, lanzar);
    } else {
        lanzar();
    }
}
